<template>
	<Accessible
		name="newmeetingpopup"
		trapped
		focusFirstItem
		autoFocus
		:wait="100"
		:tabOrder="[
			{ css: '.modal-close-btn', clickOnEnter: true },
			{ css: '.action-row:nth-child(1) .vue-js-switch', clickOnEnter: true },
			{ css: '.action-row:nth-child(3) .vue-js-switch', clickOnEnter: true },
			{ css: '.start-button', clickOnEnter: true },
		]"
	>
		<SimpleModal @close="close" class="new-meeting-popup-modal">
			<div class="new-meeting-popup container">
				<h3 v-t="'meetings_actionsheet_start_title'" />
				<div class="action-container">
					<div class="action-row">
						<div
							class="action-label"
							:class="{ 'disabled-action-label': !audio }"
						>
							<div class="action-icon" :class="{ active: audio }">
								<MicOnIcon v-if="audio" :color="activeColor" class="mic" />
								<MicOffIcon v-if="!audio" :color="inactiveColor" class="mic" />
							</div>
							{{ $t(`meetings_actionsheet_audio_${audio ? 'on' : 'off'}`) }}
						</div>
						<ToggleSwitch
							v-model="audio"
							:aria-label="
								audio
									? $t('meetings_audio_disable_alt')
									: $t('meetings_audio_enable_alt')
							"
						/>
					</div>
					<div class="divider" />
					<div class="action-row">
						<div
							class="action-label"
							:class="{ 'disabled-action-label': !video }"
						>
							<div class="action-icon" :class="{ active: video }">
								<CameraOnIcon
									v-if="video"
									:color="activeColor"
									class="camera"
								/>
								<CameraOffIcon
									v-if="!video"
									:color="inactiveColor"
									class="camera"
								/>
							</div>
							{{ $t(`meetings_actionsheet_video_${video ? 'on' : 'off'}`) }}
						</div>
						<ToggleSwitch
							v-model="video"
							:aria-label="
								video
									? $t('meetings_video_disable_alt')
									: $t('meetings_video_enable_alt')
							"
						/>
					</div>
					<FormItem
						:label="$t('meetings_captions_language_label')"
						class="language-container"
						v-if="shouldRequestLanguage"
					>
						<Dropdown :options="languageOptions" v-model="language" global />
					</FormItem>
					<button
						class="start-button"
						@click="startMeeting()"
						:aria-label="$t('meetings_actionsheet_button_start_meeting')"
						v-t="'meetings_actionsheet_button_start_meeting'"
					/>
				</div>
			</div>
		</SimpleModal>
	</Accessible>
</template>

<script>
import { SimpleModal, FormItem, Dropdown } from '@/components/common'
import { CameraOnIcon, CameraOffIcon, MicOnIcon, MicOffIcon } from '@/icons'
import { getOrganization } from '@/store'
import { ToggleSwitch } from '@/components/common'
import Accessible from '@/components/common/Accessible'
import { getLanguagesData, getLiveCaptionsLangs } from '@/helpers/firebase'
import i18n from '@/i18n'
import { storage } from '@/helpers/utils'

const activeColor = '#5a91f9'
const inactiveColor = '#c6cace'

export default {
	name: 'NewMeetingPopup',
	components: {
		SimpleModal,
		CameraOnIcon,
		CameraOffIcon,
		MicOnIcon,
		MicOffIcon,
		ToggleSwitch,
		Accessible,
		FormItem,
		Dropdown,
	},
	data() {
		const org = getOrganization()
		let language = storage.get('meetings_spoken_lang_pref')
		if (!language) {
			language = i18n.global.locale === 'en' ? 'en-US' : i18n.global.locale
		}
		return {
			video: true,
			audio: true,
			activeColor,
			inactiveColor,
			language,
			shouldRequestLanguage: org.meetings_captions_enabled,
		}
	},
	emits: ['close'],

	computed: {
		opentokLocales() {
			return getLiveCaptionsLangs()
		},
		languageOptions() {
			if (!this.shouldRequestLanguage || !this.opentokLocales) return []
			const deviceLocale = i18n.global.locale
			const languages = getLanguagesData()
			return this.opentokLocales
				.map((locale) => {
					const localeData =
						languages.langs[locale] ||
						languages.langs[locale.split('-')[0]] ||
						{}
					return { ...localeData, code: locale }
				})
				.filter((data) => data?.localizednames)
				.map(({ code, localizednames }) => ({
					id: code,
					label: localizednames[deviceLocale],
				}))
		},
	},

	methods: {
		startMeeting() {
			const { video, audio, language, $route } = this
			let path = `/meeting/${$route.params.id}?golive=true&video=${video}&audio=${audio}`
			if (language) {
				path += `&language=${language}`
			}
			EventBus.$emit('openPopout', path)
			this.close()
		},
		close() {
			this.$emit('close')
		},
	},
}
</script>

<style lang="scss" scoped>
$blue: rgb(90, 145, 249);
$gray: #c6cace;

.container {
	background-color: $blue;
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	max-width: 375px;
}

:deep(.modal-close-btn) {
	--color: white;
}

:deep(.modal-content) {
	background: transparent;
}

h3 {
	font-size: 24px;
	color: white;
	margin: 32px 32px 16px 32px;
}

.language-container {
	margin-top: 16px;
}

.action-container {
	display: flex;
	flex-direction: column;
	width: 100%;
	padding: 24px;
	background-color: white;

	.action-row {
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		.action-label {
			font-size: 16px;
			display: flex;
			align-items: center;
			font-family: 'Avenir-Heavy';
			font-weight: normal;
			color: #5a91f9;
			.action-icon {
				display: flex;
				align-items: center;
				width: 45px;
				height: 36px;
				margin-right: 7px;
				&.active {
					.camera {
						width: 37px;
						height: 24px;
						margin-left: 2px;
					}
					.mic {
						width: 22px;
						height: 34px;
						margin-left: 4px;
					}
				}
			}
		}
		.pronto-toggle {
			font-size: 27px;
			&.active {
				background: $blue;
			}
		}
	}
}

.disabled-action-label {
	color: #9ea2a6 !important;
	font-family: 'Avenir-Medium' !important;
}

.camera {
	width: 37px;
	height: 36px;
	margin-left: 1px;
}

.mic {
	width: 28px;
	height: 34px;
	margin-left: 2px;
}

a {
	text-decoration: none;
	outline: none;
	display: block;
	width: 100%;
	text-align: center;
}

.start-button {
	height: 60px;
	font-size: 17px;
	color: white;
	background: $blue;
	border-radius: 4px;
	display: block;
	width: 100%;
	text-align: center;
	margin-top: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.divider {
	border-top: 1px solid #e2e4e6;
	margin: 16px 0;
	width: 100%;
}
</style>

<style lang="scss">
.new-meeting-popup-modal {
	.modal-close-btn {
		color: white;
		top: 12px;
		right: 12px;
	}
}
</style>

<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.new-meeting-popup {
		.action-container {
			background-color: var(--neutral-200);
			.dropdown-container {
				.dropdown-selected {
					background-color: var(--neutral-100) !important;
				}
			}
		}
	}
}
</style>
